<template>
    <div>
        <div class="row">
            <div class="col-lg-4">
                <h3>Tax Invoice</h3>
            </div>
            <div class="col-lg-8">
                <a :href="downloadInvoiceLink(invoice.id)" class="sau-link-button sm py-2 float-right mt-1"><i class="fal fa-file-pdf text-danger"></i> Download Invoice</a>
                <a v-if="invoice.status == 'Unpaid'" href="#" @click.prevent="setRequestedInvoice(invoice)" v-b-modal.extension-request class="sau-link-button sm py-2 float-right mt-1 mr-4">Request Extension</a>
                <sau-request-extension></sau-request-extension>
            </div>
        </div>

        <template v-if="invoice.status === 'Overdue' && hasSuspendedServices">
            <div class="row pt-6">
                <div class="col">
                    <div class="bg-red-100 border-l-4 border-red-500 text-red-700 px-4 py-3 sau-font sm" role="alert">
                        <div>
                            <span>Services associated with this invoice have been suspended pending reciept of payment. </span>
                            <span>Reactivate services by paying all outstanding invoices.</span>
                        </div>
                    <div class="text-xs">* For immediate reactivation of suspended services, use an instant payment method (Credit Card or PayPal).</div>
                    </div>
                </div>
            </div>
        </template>

        <div class="row pt-6">
            <div class="col">
                <div class="table-responsive">
                    <table class="table table-bordered">
                        <tbody>
                            <tr>
                                <td>
                                    Servers Australia Pty Ltd <br />
                                    ABN: 98 125 037 443<br />
                                    4 Amy Close<br />
                                    Wyong NSW 2259<br />
                                    1300 788 862
                                </td>
                                <td align="right">
                                    <span class="sau-font-bold">Status:</span> {{invoice.status}}<br />
                                    <span class="sau-font-bold">Invoice Number:</span> {{invoice.id}}<br />
                                    <span class="sau-font-bold">Invoice Date: </span>{{ invoice.date | moment('DD/MM/YYYY') }}<br />
                                    <span class="sau-font-bold">Due Date:</span> {{ invoice.duedate | moment('DD/MM/YYYY') }}<br />
                                    <span v-if="invoice.status == 'Paid'">
                                        <span class="sau-font-bold">Date Paid:</span> {{ invoice.datepaid | moment('DD/MM/YYYY') }}
                                    </span>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>

        <div class="row pt-2">
            <div class="col">
                <div class="table-responsive">
                    <table class="table table-bordered">
                        <tbody>
                            <tr>
                                <td>
                                    <h5 class="sau-pm-reset">Billing Contact</h5>
                                </td>
                            </tr>
                            <tr>
                                <td v-html="formattedAddress"></td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>

        <div class="row pt-2">
            <div class="col">
                <div class="table-responsive">
                    <table class="table table-bordered">
                        <tbody>
                            <tr>
                                <td>
                                    <h5 class="sau-pm-reset">Description</h5>
                                </td>
                                <td align="right">
                                    <h5 class="sau-pm-reset">Amount</h5>
                                </td>
                            </tr>
                            <tr v-for="item in invoice.items" :key="item.id">
                                <td>
                                    <div v-html="item.description"></div>
                                    <template v-if="invoice.status === 'Overdue' && item.hosting && item.hosting.domainstatus === 'Suspended'">
                                        <div class="mt-3 text-red-400 text-xs">This service has been suspended. Please resolve outstanding overdue invoices to reactivate this service.</div>
                                    </template>
                                </td>
                                <td align="right" width="28%">
                                    {{item.amount | currency}}
                                </td>
                            </tr>
                            <tr class="sau-font-bold">
                                <td align="right">
                                    Sub total
                                </td>
                                <td align="right" width="28%">
                                    {{invoice.subtotal | currency}}
                                </td>
                            </tr>
                            <tr class="sau-font-bold">
                                <td align="right">
                                    +10% GST
                                </td>
                                <td align="right" width="28%">
                                    {{invoice.tax | currency}}
                                </td>
                            </tr>
                            <tr class="sau-font" v-for="(credit, index) in invoice.credits" :key="index">
                                <td align="right">
                                    {{ credit.description }} - {{ credit.date |  moment('DD/MM/YYYY') }}
                                </td>
                                <td align="right" width="28%" class="sau-font-bold">
                                    {{ credit.amount | currency }}
                                </td>
                            </tr>
                            <tr class="sau-font-bold sau-font lg">
                                <td align="right">
                                    Total
                                </td>
                                <td align="right" width="28%">
                                    {{invoice.total | currency}}
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>

        <h3 class="mt-12">Transactions</h3>
        <div v-if="!Object.keys(invoice.transactions).length">
            <p>Any recent transactions for this invoice will be listed below. If you believe that there has been an error, please <a href="/support/conversations">get in touch</a> and we can resolve this for you.</p>
        </div>
        <div v-else>
            <p>Below are all your recent transactions for this invoice. If you believe that there has been an error, please <a href="/support/conversations">get in touch</a> and we can resolve this for you.</p>
            <div class="row pt-2">

                <div class="col">
                    <div class="table-responsive">
                        <table class="table table-bordered">
                            <tbody>
                                <tr>
                                    <td>
                                        <h5 class="sau-pm-reset">Description</h5>
                                    </td>
                                    <td align="right" width="28%">
                                        <h5 class="sau-pm-reset">Amount</h5>
                                    </td>
                                </tr>
                                <tr v-for="(transaction, index) in filteredTransactions" :key="index" class="transaction">
                                    <td :class="index >= 4 && !showAllTransactions ? 'd-none' : ''">{{ transaction.description }}<br>
                                            <span class="sau-font-bold">Date:</span> {{ transaction.date | moment('DD/MM/YYYY - h:mma') }}<br>
                                            <span class="sau-font-bold">Gateway:</span> {{ transaction.gateway }}<br>
                                            <span class="sau-font-bold">Transaction ID:</span> {{ transaction.transid }}<br>
                                            </td>
                                    <td :class="index >= 4 && !showAllTransactions ? 'd-none' : ''" align="right" class="sau-font-bold">
                                        {{ (Math.round(transaction.amountin) ? '$' + transaction.amountin :
                                        (!Math.round(transaction.amountin) && !Math.round(transaction.amountout) ? '$' + transaction.amountin : '- $' +transaction.amountout )) }}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
            <template v-if="filteredTransactions.length > 4">
                <a href="#" @click.prevent="toggleAllTransactions()">{{ showAllTransactions ? 'See fewer' : 'See all' }} transactions</a>
            </template>
            <p></p>
        </div>

        <h3 class="mt-12">Transaction History</h3>
        <div v-if="Object.keys(invoice.transaction_history).length">
            <p>Below are any recent failed transactions for this invoice. If you believe that there has been an error, please <a href="/support/conversations">get in touch</a> and we can resolve this for you.</p>
            <div class="row pt-2">

                <div class="col">
                    <div class="table-responsive">
                        <table class="table table-bordered">
                            <tbody>
                                <tr>
                                    <td>
                                        <h5 class="sau-pm-reset">Description</h5>
                                    </td>
                                    <td align="right" width="28%">
                                        <h5 class="sau-pm-reset">Amount</h5>
                                    </td>
                                </tr>
                                <tr v-for="(transaction, index) in invoice.transaction_history" :key="index" class="transaction">
                                    <td>{{ transaction.description }}<br>
                                            <span class="sau-font-bold">Date:</span> {{ transaction.created_at | moment('DD/MM/YYYY - h:mma') }}<br>
                                            <span class="sau-font-bold">Gateway:</span> {{ transaction.gateway }}<br>
                                            <span class="sau-font-bold">Status:</span> {{ transaction.remote_status }}<br>
                                            <span class="sau-font-bold">Transaction ID:</span> {{ transaction.transaction_id }}<br>
                                            </td>
                                    <td align="right" class="sau-font-bold">
                                        {{ transaction.amount | currency }}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>



    </div>
</template>

<script>
export default {
    data() {
        return {
            requested: '',
            showAllTransactions: false,
        }
    },
    methods:{
        setRequestedInvoice(invoice) {
            this.requested = invoice;
        },
        toggleAllTransactions() {
            this.showAllTransactions = !this.showAllTransactions;
        },
    },
    computed: {
        formattedAddress() {

            if(this.userDetails.billing) { // this is here because computed was running before the vuex store was finished loading, and accessing nested properties throws errors
                if(this.userDetails.billing.contact_details) {

                    return Array.from([
                        `${this.userDetails.billing.contact_details.firstname} ${this.userDetails.billing.contact_details.lastname}`,
                        `${this.userDetails.billing.contact_details.address1} ${this.userDetails.billing.contact_details.address2}`,
                        `${this.userDetails.billing.contact_details.city}, ${this.userDetails.billing.contact_details.state}, ${this.userDetails.billing.contact_details.postcode}`,
                        this.userDetails.billing.contact_details.phonenumber
                    ]).join('<br>');

                } else {

                    return Array.from([
                        `${this.userDetails.firstname} ${this.userDetails.lastname}`,
                        `${this.userDetails.address1} ${this.userDetails.address2}`,
                        `${this.userDetails.city}, ${this.userDetails.state}, ${this.userDetails.postcode} `,
                        this.userDetails.phonenumber
                    ]).join('<br>');

                }
            }
        },
        filteredTransactions() {
            return this.invoice.transactions.reverse();
        },
        suspendedServices() {
            return this.invoice.items.filter(item => item.hosting && item.hosting.domainstatus === 'Suspended');
        },
        hasSuspendedServices() {
            return this.suspendedServices.length > 0;
        }
    }
}
</script>
